import React, { useState } from "react";
import moment from "moment";
import DateRangePicker from "@market/components/DateRangePicker/DateRangePicker";
import Dropdown from "@appComponents/Dropdown/Dropdown";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";

import ITransactionHistoryNav from "./ITransactionHistoryNav";
import { IWalletDetails } from "@market/interfaces/IWallet";
import { renameMainCurrency } from "@features/utils/functions";
import DownloadIcon from "@assets/svg/download.svg";

import * as style from "./TransactionHistoryNavStyle.module.scss";
import { BottomSheet } from "@appComponents/BottomSheet";
import DateRangePickerV2 from "@market/components/DateRangePickerV2/DateRangePickerV2";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import SelectV2 from "@appComponents/SelectV2";

const TransactionHistoryNav: React.FunctionComponent<ITransactionHistoryNav> =
  React.memo((props) => {
    const { wallets, currency, startDate, endDate, handleOpenDownloadModal } =
      props;
    const [isDateSelectOpen, setIsDateSelectOpen] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 768 });

    const dropdownData = wallets
      ? wallets.map((el: IWalletDetails) => {
          return {
            id: el.id,
            name: renameMainCurrency(el.currency).toUpperCase(),
            slug: el.currency,
          };
        })
      : [];

    dropdownData.unshift({
      id: 0,
      name: isMobile ? "All" : "Select...",
      slug: "",
    });

    const filtersCount = [currency, startDate, endDate].reduce(
      (acc: number, el: undefined | string | Date) => {
        return acc + (el ? 1 : 0);
      },
      0
    );

    const currentWallet = wallets
      ? wallets.find((el) => el.currency === props.currency)
      : undefined;

    const handleOpenDateSelect = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setIsDateSelectOpen(true);
    };

    return (
      <div className={style.transactionHistoryNav}>
        <div className={style.values}>
          {currentWallet && (
            <>
              <div className={style.balance}>
                <p>Balance</p>
                <CurrencyElement
                  value={currentWallet!.current_balance}
                  currency={currency}
                  bold={true}
                />
              </div>
              <div className={style.balance}>
                <p>Available Balance</p>
                <CurrencyElement
                  value={currentWallet!.balance}
                  currency={currency}
                  bold={true}
                />
              </div>
            </>
          )}
        </div>
        <div className={style.filters}>
          {!isMobile && filtersCount > 0 && (
            <p className={style.clear} onClick={props.clearFilters}>
              {`Clear\xa0filter${
                filtersCount > 1 ? "s" : ""
              }\xa0(${filtersCount})`}
            </p>
          )}
          <div className={style.customSelect}>
            <p>Currency</p>
            <Dropdown
              currency={currency}
              initialValue="All"
              className={classNames({
                "rounded-[8px]": isMobile,
              })}
              data={dropdownData}
              update={props.updateCurrency}
            />
          </div>
          {isMobile && (
            <>
              <div className={classNames(style.customSelect, "flex-col")}>
                <p>Dates</p>
                <div
                  className="border border-grey-lighter rounded-[8px] text-xs text-[#333] p-4 cursor-pointer"
                  onClick={handleOpenDateSelect}
                >
                  {startDate || endDate
                    ? `${
                        startDate ? moment(startDate).format("D MMM YYYY") : "-"
                      } - ${
                        endDate ? moment(endDate).format("D MMM YYYY") : "-"
                      }`
                    : "All"}
                </div>
              </div>

              <div className="flex-col inline-block">
                <div className="h-[36px]" />
                <div
                  className="border border-grey-lighter rounded-[8px] text-xs text-[#333] p-4 cursor-pointer inline-block"
                  onClick={handleOpenDownloadModal}
                >
                  <DownloadIcon />
                </div>
              </div>
            </>
          )}
          {!isMobile && (
            <DateRangePicker
              updateState={props.updateState}
              startDate={startDate && new Date(startDate)}
              endDate={endDate && new Date(endDate)}
              maxDate={new Date(moment().format())}
              minDate={new Date(moment("2012-01-01").format())}
              notSelected={true}
            />
          )}
        </div>

        {isMobile && (
          <BottomSheet
            title="Date selection"
            open={isDateSelectOpen}
            onClose={() => setIsDateSelectOpen(false)}
          >
            <DateRangePickerV2
              updateState={props.updateState}
              startDate={startDate && new Date(startDate)}
              endDate={endDate && new Date(endDate)}
              maxDate={new Date(moment().format())}
              minDate={new Date(moment("2012-01-01").format())}
              notSelected
              onClose={() => setIsDateSelectOpen(false)}
            />
          </BottomSheet>
        )}
      </div>
    );
  });

export default TransactionHistoryNav;
